import { compareText } from './compareText'
import { daysApart } from './months'

export interface PairCandidate {
  id: string
  date: string
}

interface CandidatePair<Left, Right> {
  left: Left
  right: Right
  days: number
}

export interface ClosestPairingRule<Left, Right> {
  maxDaysApart: number
  canPair: (left: Left, right: Right) => boolean
  // Extra dates a side may be measured from (a trade's settle date), closest wins.
  datesOf?: (side: Left | Right) => string[]
}

const closestDays = <Left extends PairCandidate, Right extends PairCandidate>(
  left: Left,
  right: Right,
  rule: ClosestPairingRule<Left, Right>,
): number => {
  const leftDates = rule.datesOf?.(left) ?? [left.date]
  const rightDates = rule.datesOf?.(right) ?? [right.date]
  let closest = Number.POSITIVE_INFINITY
  for (const leftDate of leftDates) {
    for (const rightDate of rightDates) {
      closest = Math.min(closest, daysApart(leftDate, rightDate))
    }
  }
  return closest
}

const candidatePairs = <
  Left extends PairCandidate,
  Right extends PairCandidate,
>(
  lefts: Left[],
  rights: Right[],
  rule: ClosestPairingRule<Left, Right>,
): CandidatePair<Left, Right>[] => {
  const pairs: CandidatePair<Left, Right>[] = []
  for (const left of lefts) {
    for (const right of rights) {
      if (!rule.canPair(left, right)) continue
      const days = closestDays(left, right, rule)
      if (days <= rule.maxDaysApart) pairs.push({ left, right, days })
    }
  }
  return pairs.sort(
    (first, second) =>
      first.days - second.days ||
      compareText(first.left.date, second.left.date) ||
      compareText(first.left.id, second.left.id) ||
      compareText(first.right.id, second.right.id),
  )
}

// lukita's Agent::InternalTransferMatcher rule: closest dates pair first and each row pairs once.
export const pairClosestFirst = <
  Left extends PairCandidate,
  Right extends PairCandidate,
>(
  lefts: Left[],
  rights: Right[],
  rule: ClosestPairingRule<Left, Right>,
): [Left, Right][] => {
  const used = new Set<string>()
  const pairs: [Left, Right][] = []
  for (const { left, right } of candidatePairs(lefts, rights, rule)) {
    if (used.has(left.id) || used.has(right.id)) continue
    used.add(left.id)
    used.add(right.id)
    pairs.push([left, right])
  }
  return pairs
}

// Stricter than closest-first: a row with two partners at its closest distance is a guess, so
// neither pairs. Each side must be the other's one closest candidate.
export const pairUnambiguous = <
  Left extends PairCandidate,
  Right extends PairCandidate,
>(
  lefts: Left[],
  rights: Right[],
  rule: ClosestPairingRule<Left, Right>,
): [Left, Right][] => {
  const candidates = candidatePairs(lefts, rights, rule)
  const closest = new Map<string, { days: number; partners: string[] }>()
  const note = (id: string, partnerId: string, days: number) => {
    const current = closest.get(id)
    if (!current || days < current.days) {
      closest.set(id, { days, partners: [partnerId] })
    } else if (days === current.days) {
      current.partners.push(partnerId)
    }
  }
  for (const { left, right, days } of candidates) {
    note(left.id, right.id, days)
    note(right.id, left.id, days)
  }
  const isOnlyClosest = (id: string, partnerId: string) => {
    const partners = closest.get(id)?.partners ?? []
    return partners.length === 1 && partners[0] === partnerId
  }
  return candidates
    .filter(
      ({ left, right }) =>
        isOnlyClosest(left.id, right.id) && isOnlyClosest(right.id, left.id),
    )
    .map(({ left, right }) => [left, right])
}

// Closest first, but a pick is a guess when a left row nobody picked was just as close to the same
// right row. Such a pick is dropped, so a tie pairs nothing.
export const pairClosestUnlessTied = <
  Left extends PairCandidate,
  Right extends PairCandidate,
>(
  lefts: Left[],
  rights: Right[],
  rule: ClosestPairingRule<Left, Right>,
): [Left, Right][] => {
  const pairs = pairClosestFirst(lefts, rights, rule)
  const pickedLefts = new Set(pairs.map(([left]) => left.id))
  const unpicked = lefts.filter((left) => !pickedLefts.has(left.id))
  const isTied = (left: Left, right: Right) => {
    const days = closestDays(left, right, rule)
    return unpicked.some(
      (other) =>
        rule.canPair(other, right) && closestDays(other, right, rule) === days,
    )
  }
  return pairs.filter(([left, right]) => !isTied(left, right))
}
